import { designLayers } from '../interaction';
import { useId, type ReactNode } from 'react';
import { CloseOutlined } from '@mui/icons-material';
import { Box, Divider, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { designTokens } from '../tokens';

export type DetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  titleId?: string;
  meta?: ReactNode;
  status?: ReactNode;
  summary?: ReactNode;
  headerAction?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  closeLabel?: string;
};

function DetailContent({
  onClose,
  title,
  titleId: resolvedTitleId,
  metaId: generatedMetaId,
  meta,
  status,
  summary,
  headerAction,
  footer,
  children,
  closeLabel = '상세 닫기',
  presentation,
}: Omit<DetailDrawerProps, 'open'> & { titleId: string; metaId: string; presentation: 'modal' | 'inline' }) {
  const hasMeta = meta !== undefined && meta !== null;
  const hasStatus = status !== undefined && status !== null;
  const hasHeaderAction = headerAction !== undefined && headerAction !== null;
  const hasFooter = footer !== undefined && footer !== null;
  return (
      <Box sx={{ width: '100%', minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack component="header" direction="row" spacing={1} sx={{
          alignItems: 'center', flexShrink: 0,
          minHeight: designTokens.layout.detailHeaderMinHeight,
          py: `${designTokens.layout.detailHeaderBlockInset}px`,
          pl: `${designTokens.layout.detailInlineInset}px`,
          pr: `${designTokens.layout.detailHeaderBlockInset}px`,
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', minWidth: 0, flexWrap: 'wrap' }}>
              <Typography id={resolvedTitleId} component="h2" sx={{ ...(presentation === 'inline' ? designTokens.typography.panelTitle : designTokens.typography.sectionTitle), minWidth: 0, overflowWrap: 'anywhere' }}>{title}</Typography>
              {hasStatus ? <Box sx={{ flexShrink: 0 }}>{status}</Box> : null}
            </Stack>
            {hasMeta ? <Typography id={generatedMetaId} component="div" variant="caption" color="text.secondary" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{meta}</Typography> : null}
          </Box>
          {hasHeaderAction ? <Box sx={{ flexShrink: 0 }}>{headerAction}</Box> : null}
          <Tooltip title={closeLabel}><IconButton aria-label={closeLabel} onClick={onClose} sx={{ flexShrink: 0 }}><CloseOutlined /></IconButton></Tooltip>
        </Stack>
        <Divider />
        <Box data-slot="body" sx={{ flex: 1, minHeight: 0, overflow: 'auto', overscrollBehavior: 'contain' }}>{summary}{children}</Box>
        {hasFooter ? <><Divider /><Box data-slot="footer" sx={{
          flexShrink: 0, minHeight: 0, maxHeight: '50%', overflow: 'auto', overscrollBehavior: 'contain',
          p: `${designTokens.layout.detailInlineInset}px`, bgcolor: designTokens.color.canvas,
        }}>{footer}</Box></> : null}
      </Box>
  );
}

/** 작은 화면이나 작업 중 맥락을 고정해야 하는 모달 상세입니다. */
export function DetailDrawer({ open, onClose, titleId, ...content }: DetailDrawerProps) {
  const generatedTitleId = useId();
  const generatedMetaId = useId();
  const resolvedTitleId = titleId ?? generatedTitleId;
  const hasMeta = content.meta !== undefined && content.meta !== null;
  return <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    variant="temporary"
    sx={{ zIndex: designLayers.detail }}
    slotProps={{
      paper: {
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': resolvedTitleId,
        'aria-describedby': hasMeta ? generatedMetaId : undefined,
        sx: {
          width: designTokens.layout.detailPanelWidth, maxWidth: '92vw', boxSizing: 'border-box', overflow: 'hidden',
          borderRadius: designTokens.shape.none,
          bgcolor: designTokens.color.canvas, boxShadow: designTokens.shadow.overlay,
        },
      },
      backdrop: { sx: { backgroundColor: designTokens.overlay.detailBackdrop } },
    }}
  >
    <DetailContent {...content} presentation="modal" onClose={onClose} titleId={resolvedTitleId} metaId={generatedMetaId} />
  </Drawer>;
}

export type DetailPanelProps = DetailDrawerProps & {
  /** 독립된 작업 영역은 카드 표면으로 표시합니다. */
  appearance?: 'inline' | 'card';
};

/** 목록 옆에 배치하는 비모달 상세입니다. 선택과 닫기 후 초점 복귀는 호출자가 소유합니다. */
export function DetailPanel({ open, onClose, titleId, appearance = 'inline', ...content }: DetailPanelProps) {
  const generatedTitleId = useId();
  const generatedMetaId = useId();
  const resolvedTitleId = titleId ?? generatedTitleId;
  const hasMeta = content.meta !== undefined && content.meta !== null;
  if (!open) return null;
  return <Box
    component="aside"
    role="region"
    aria-labelledby={resolvedTitleId}
    aria-describedby={hasMeta ? generatedMetaId : undefined}
    sx={{ width: '100%', height: '100%', minWidth: 0, minHeight: 0, boxSizing: 'border-box', overflow: 'hidden', bgcolor: designTokens.color.canvas,
      ...(appearance === 'card'
        ? { border: `1px solid ${designTokens.color.divider}`, borderRadius: designTokens.radius.surface, boxShadow: designTokens.shadow.surface }
        : { borderInlineStart: `1px solid ${designTokens.color.divider}`, borderRadius: designTokens.shape.none, boxShadow: designTokens.depth.none }),
    }}
  >
    <DetailContent {...content} presentation="inline" onClose={onClose} titleId={resolvedTitleId} metaId={generatedMetaId} />
  </Box>;
}
